import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/StatusPill";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { can, normRole } from "@/lib/permissions";
import { toast } from "sonner";
import { format } from "date-fns";
import { ArrowLeft, FileDown, Edit3, ClipboardList, User, Package, Calendar, MessageSquare, Link2, Send } from "lucide-react";
import { ProductionStepper } from "@/components/ProductionStepper";
import ImageLightbox from "@/components/ImageLightbox";

const NEXT_STATUS = {
  new: ["accepted", "on-hold", "cancelled"],
  accepted: ["in-progress", "on-hold", "cancelled"],
  "in-progress": ["completed", "on-hold", "ready"],
  "on-hold": ["accepted", "in-progress", "cancelled"],
  completed: ["delivered"],
};

export default function OrderDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { images: [...], startIndex: n }
  const [statusOpen, setStatusOpen] = useState(false);
  const [statusForm, setStatusForm] = useState({ status: "", remarks: "" });

  const load = async () => {
    try { const { data } = await api.get(`/orders/${id}`); setOrder(data); } catch (_) { toast.error("Failed to load order"); }
  };
  useEffect(() => { load(); }, [id]);

  const download = async (kind) => {
    try {
      const res = await api.get(`/orders/${id}/pdf/${kind}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = `${kind}-${order.reference_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("PDF download failed"); }
  };

  const submitStatus = async () => {
    if (!statusForm.status || !statusForm.remarks.trim()) { toast.error("Status & remarks are required"); return; }
    try {
      await api.post(`/orders/${id}/status`, statusForm);
      toast.success("Status updated");
      setStatusOpen(false); setStatusForm({ status: "", remarks: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!order) return <div className="text-center py-16 text-[#4A5A50]">Loading…</div>;

  const canEdit = can(user, "orders.edit");
  const role = normRole(user?.role);
  const canUpdateStatus = can(user, "orders.update_status") || (role === "production" && order.karigar_id === user.id);
  const canSeeClient = can(user, "orders.see_client");
  const canShareTracker = can(user, "orders.edit");
  const nextStatuses = NEXT_STATUS[order.status] || ["accepted","in-progress","completed","on-hold"];

  const copyTrackerLink = async () => {
    try {
      const { data } = await api.post(`/orders/${id}/tracker-token`);
      const origin = (process.env.REACT_APP_PUBLIC_APP_URL || window.location.origin).replace(/\/$/, "");
      const link = `${origin}/track/${data.token}`;
      let copied = false;
      // navigator.clipboard requires a secure context AND a user gesture. When it's not
      // available (older browsers / non-https / iframe), fall back to a textarea trick.
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(link);
          copied = true;
        }
      } catch (err) { void err; }
      if (!copied) {
        const ta = document.createElement("textarea");
        ta.value = link; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        try { copied = document.execCommand("copy"); } catch (e) { void e; }
        document.body.removeChild(ta);
      }
      if (copied) toast.success("Tracker link copied");
      else window.prompt("Copy this tracker link:", link);
    } catch (e) {
      toast.error("Failed to create tracker link");
    }
  };

  /** Resolve the recipient phone for a WhatsApp share — order object doesn't hydrate
   * client_mobile, so we fetch the contact on demand when needed. */
  const resolveRecipientPhone = async (kind) => {
    try {
      if (kind === "order-sheet" && order?.client_id) {
        const { data } = await api.get(`/contacts/${order.client_id}`);
        return data?.mobile || data?.whatsapp_number || "";
      }
      if (kind === "karigar-sheet" && order?.karigar_id) {
        const { data } = await api.get(`/users`);
        const arr = Array.isArray(data) ? data : (data?.items || []);
        const k = arr.find(u => u.id === order.karigar_id);
        return k?.phone || k?.mobile || "";
      }
    } catch (err) { void err; }
    return "";
  };

  const shareOnWhatsApp = async (kind) => {
    try {
      const { data: tokData } = await api.post(`/orders/${id}/tracker-token`);
      const origin = (process.env.REACT_APP_PUBLIC_APP_URL || window.location.origin).replace(/\/$/, "");
      const trackerLink = `${origin}/track/${tokData.token}`;
      const phone = await resolveRecipientPhone(kind);
      const cleanPhone = (phone || "").replace(/\D/g, "");
      const isKarigar = kind === "karigar-sheet";
      const message = isKarigar
        ? `Karigar Job Sheet — Order ${order?.reference_number || ""}\n\nTrack: ${trackerLink}\n\n(Download from the app)`
        : `Namaste! Your TARU order ${order?.reference_number || ""} is on the way.\n\nTrack live: ${trackerLink}\n\n(Order sheet PDF: open the app)`;
      const url = cleanPhone
        ? `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`
        : `https://wa.me/?text=${encodeURIComponent(message)}`;
      // window.open can be blocked; fall back to assigning location.
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) window.location.href = url;
    } catch (e) {
      toast.error("Failed to prepare WhatsApp share");
    }
  };

  return (
    <div className="space-y-6" data-testid="order-detail-page">
      <button onClick={() => nav(-1)} className="text-sm text-[#4A5A50] hover:text-[#0F1E15] inline-flex items-center gap-1"><ArrowLeft size={14}/> Back</button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Order</div>
          <h1 className="font-serif text-4xl font-medium mt-1" data-testid="order-ref">{order.reference_number}</h1>
          <div className="mt-2 flex flex-wrap gap-2">
            <StatusPill status={order.priority}/>
            <StatusPill status={order.status}/>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canShareTracker && (
            <Button onClick={copyTrackerLink} variant="outline" className="rounded-full border-[#C9A24B] text-[#6E5416] hover:bg-[#F6EED8]" data-testid="share-tracker-btn">
              <Link2 size={14} className="mr-1.5"/> Share Tracker
            </Button>
          )}
          {canUpdateStatus && (
            <Button onClick={() => setStatusOpen(true)} variant="outline" className="rounded-full" data-testid="update-status-btn">
              <ClipboardList size={14} className="mr-1.5"/> Update status
            </Button>
          )}
          {canEdit && (
            <Button onClick={() => nav(`/orders/${id}/edit`)} variant="outline" className="rounded-full" data-testid="edit-order-btn">
              <Edit3 size={14} className="mr-1.5"/> Edit
            </Button>
          )}
          <Button onClick={() => download("order-sheet")} className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid="pdf-order-sheet">
            <FileDown size={14} className="mr-1.5"/> Order Sheet
          </Button>
          <Button onClick={() => shareOnWhatsApp("order-sheet")} variant="outline" className="rounded-full border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10" data-testid="share-order-wa">
            <Send size={14} className="mr-1.5"/> Share on WhatsApp
          </Button>
          <Button onClick={() => download("karigar-sheet")} variant="outline" className="rounded-full" data-testid="pdf-karigar-sheet">
            <FileDown size={14} className="mr-1.5"/> Karigar Sheet
          </Button>
          <Button onClick={() => shareOnWhatsApp("karigar-sheet")} variant="outline" className="rounded-full border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10" data-testid="share-karigar-wa">
            <Send size={14} className="mr-1.5"/> Share Karigar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 border-[#E4DFCF] rounded-xl">
          <CardContent className="p-6 space-y-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A5A50] mb-3">Items</div>
              <div className="space-y-3">
                {order.items?.map((it, i) => (
                  <div key={it.id || i} className="border border-[#E4DFCF] rounded-xl p-4" data-testid={`order-item-${i}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-serif text-lg">{it.product_category_name || "—"} <span className="text-sm text-[#4A5A50]">× {it.quantity}</span></div>
                        <div className="text-xs text-[#4A5A50] mt-0.5 uppercase tracking-widest">{it.order_type}</div>
                      </div>
                      {it.delivery_date && <div className="text-xs text-[#4A5A50] flex items-center gap-1"><Calendar size={12}/>{format(new Date(it.delivery_date), "dd MMM yyyy")}</div>}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-sm">
                      <div><div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Measurement</div><div>{it.measurement_value || "—"}</div></div>
                      <div><div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Color</div><div>{it.color || "—"}</div></div>
                      <div><div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Weight</div><div>{it.weight_from || "—"} - {it.weight_to || "—"} g</div></div>
                      <div><div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Melting</div><div>{it.melting || "—"}</div></div>
                    </div>
                    <div className="mt-2 text-xs text-[#4A5A50]">HUID: {it.huid ? "Yes" : "No"}</div>
                    {it.images?.length ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {it.images.map((img, j) => (
                          <button key={j} onClick={() => setLightbox({ images: it.images, startIndex: j })} className="w-24 h-24 md:w-28 md:h-28 overflow-hidden rounded-lg border border-[#E4DFCF] hover:ring-2 hover:ring-[#0F5E39]/40 transition" data-testid={`item-image-${i}-${j}`}>
                            <img src={fileUrl(img.thumb_url || img.url)} alt="" loading="lazy" className="w-full h-full object-cover"/>
                          </button>
                        ))}
                      </div>
                    ) : null}
                    {/* 7-department production stepper — visible to all internal roles; production users can see progress on their own items. Client viewers can also see it. */}
                    <ProductionStepper orderItemId={it.id} hidePricing={!canSeeClient}/>
                  </div>
                ))}
              </div>
            </div>
            {order.remarks && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A5A50] mb-2">Remarks</div>
                <div className="text-sm bg-[#F1EDDF] rounded-lg p-3">{order.remarks}</div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card className="border-[#E4DFCF] rounded-xl">
            <CardContent className="p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A5A50] mb-3">People</div>
              <div className="space-y-3 text-sm">
                {canSeeClient && (
                  <div className="flex items-center gap-2"><User size={14} className="text-[#C9A24B]"/><div><div className="text-[10px] uppercase text-[#4A5A50]">Client</div><div>{order.client_name || "—"}</div></div></div>
                )}
                <div className="flex items-center gap-2"><User size={14} className="text-[#C9A24B]"/><div><div className="text-[10px] uppercase text-[#4A5A50]">Karigar</div><div>{order.karigar_name || "—"}</div></div></div>
                <div>
                  <div className="text-[10px] uppercase text-[#4A5A50] mb-1">Assigned staff</div>
                  <div className="flex flex-wrap gap-1">
                    {(order.staff || []).map(s => <span key={s.id} className="text-xs bg-[#F6EED8] text-[#6E5416] px-2 py-1 rounded-full">{s.name}</span>)}
                    {(!order.staff || order.staff.length === 0) && <span className="text-xs text-[#4A5A50]">None</span>}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#E4DFCF] rounded-xl">
            <CardContent className="p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A5A50] mb-3">Timeline</div>
              <div className="relative pl-4">
                <div className="absolute left-1 top-1 bottom-1 w-px bg-[#E4DFCF]"/>
                {(order.logs || []).map((l, i) => (
                  <div key={l.id || i} className="relative pb-4">
                    <div className="absolute -left-3 top-1 w-2 h-2 rounded-full bg-[#C9A24B]"/>
                    <div className="flex items-center gap-2 mb-1">
                      <StatusPill status={l.status}/>
                    </div>
                    <div className="text-sm">{l.remarks}</div>
                    <div className="text-[10px] uppercase text-[#4A5A50] mt-0.5 tracking-widest">
                      {l.user_name} · {format(new Date(l.created_at), "dd MMM yyyy, HH:mm")}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Lightbox */}
      <ImageLightbox
        open={!!lightbox}
        images={lightbox?.images || []}
        startIndex={lightbox?.startIndex || 0}
        onClose={() => setLightbox(null)}
        resolveSrc={(img) => fileUrl(img.url || img.thumb_url)}
      />

      {/* Status modal */}
      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent data-testid="status-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Update status</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Status</Label>
              <Select value={statusForm.status} onValueChange={(v)=>setStatusForm({...statusForm, status:v})}>
                <SelectTrigger data-testid="status-select"><SelectValue placeholder="Pick status"/></SelectTrigger>
                <SelectContent>
                  {nextStatuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Remarks (required)</Label>
              <Textarea rows={3} value={statusForm.remarks} onChange={(e)=>setStatusForm({...statusForm, remarks:e.target.value})} data-testid="status-remarks"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusOpen(false)}>Cancel</Button>
            <Button onClick={submitStatus} className="bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid="status-submit">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
